import { DataType } from "@src/features/analytics/types";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";

export type SitewideTableConfig = {
  id?: string;
  key: string;
  label: string;
  format: DataType;
  rowStyle?: string;
  showCollapsibleSection?: (any) => boolean;
  excludeFromTable?: (params: excludeFromTableParams) => boolean;
  dropDownGroup?: { name: string; type: "item" | "container" };
};

export type SitewideTableRow = {
  metric: string;
} & Omit<SitewideTableConfig, "label" | "format">;

// TODO: better typing here
export type excludeFromTableParams = {
  row: SitewideTableRow;
  dates: any;
  dataByDate: any;
  sitewideDashboardFilters: ISitewideDashboardFilters;
};

const excludeNoDataFromTable = (params: excludeFromTableParams) => {
  return (
    params.dates.filter((date) => params.dataByDate[date][params.row.key] !== 0)
      .length > 0
  );
};

const excludeOnAllOrders = (params: excludeFromTableParams) => {
  return params.sitewideDashboardFilters.ordersMatchedToSession === "allOrders";
};

const excludeOnOrdersMatchedToSession = (params: excludeFromTableParams) => {
  return (
    params.sitewideDashboardFilters.ordersMatchedToSession ===
    "ordersMatchedToSession"
  );
};

export const metricsConfig: SitewideTableConfig[] = [
  {
    key: "cvr",
    label: "CVR",
    format: "percentTwoDecimal",
    rowStyle: "bold",
    dropDownGroup: { name: "cvr", type: "container" },
  }, // BOLD
  {
    key: "n_sessions",
    label: "Sessions",
    format: "numberNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "cvr", type: "item" },
  },
  {
    key: "n_visitors",
    label: "Visitors",
    format: "numberNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "cvr", type: "item" },
  },
  {
    key: "n_orders",
    label: "Orders matched to session",
    format: "numberNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "cvr", type: "item" },
  },
  {
    key: "n_orders",
    label: "Total Orders",
    format: "numberNoDecimal",
    rowStyle: "bold",
    dropDownGroup: { name: "n_orders", type: "container" },
    excludeFromTable: excludeOnAllOrders,
  },
  {
    key: "recurring_sub",
    label: "Recurring subscription orders",
    format: "numberNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "n_orders", type: "item" },
    excludeFromTable: excludeOnAllOrders,
  },
  {
    key: "draft_orders",
    label: "Draft Orders",
    format: "numberNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "n_orders", type: "item" },
    excludeFromTable: excludeOnAllOrders,
  },
  {
    key: "non_online_store_orders",
    label: "Non-online store orders",
    format: "numberNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "n_orders", type: "item" },
    excludeFromTable: excludeOnAllOrders,
  },
  {
    key: "orders_other",
    label: "Other",
    format: "numberNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "n_orders", type: "item" },
    excludeFromTable: excludeOnAllOrders,
  },
  {
    key: "net_revenue",
    label: "Net Revenue",
    format: "currencyNoDecimal",
    rowStyle: "bold",
    dropDownGroup: { name: "net_revenue", type: "container" },
  },
  {
    key: "gross_revenue",
    label: "Gross Revenue",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "net_revenue", type: "item" },
  },
  {
    key: "lost_net_revenue",
    label: "Returns",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "net_revenue", type: "item" },
  }, // lost_orders ?
  {
    key: "discounts",
    label: "Discounts",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => true,
    dropDownGroup: { name: "net_revenue", type: "item" },
  },
  {
    key: "aov",
    label: "AOV",
    format: "currency",
    rowStyle: "bold",
    dropDownGroup: { name: "aov", type: "container" },
  },
  {
    key: "units_per_order",
    label: "Units per Order",
    format: "number",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "aov", type: "item" },
  },
  {
    key: "gross_revenue_per_unit",
    label: "Gross Revenue per Unit",
    format: "currency",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "aov", type: "item" },
  },
  {
    key: "discount_per_unit",
    label: "Discount per Unit",
    format: "currency",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "aov", type: "item" },
  },
  {
    key: "net_revenue_per_unit",
    label: "Net Revenue per Unit",
    format: "currency",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "aov", type: "item" },
  },
  {
    key: "net_revenue_per_visitor",
    label: "Net Revenue per Visitor",
    format: "currency",
    rowStyle: "bold",
    excludeFromTable: excludeOnOrdersMatchedToSession,
  },
  {
    key: "cost_of_revenue",
    label: "Cost of Revenue",
    format: "currencyNoDecimal",
    rowStyle: "bold",
    dropDownGroup: { name: "cost_of_revenue", type: "container" },
  },
  {
    key: "order_cogs",
    label: "Product COGS",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "cost_of_revenue", type: "item" },
  },
  {
    key: "shipping_cost",
    label: "Shipping Cost",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "cost_of_revenue", type: "item" },
  },

  {
    key: "transaction_fees",
    label: "Transaction Fees",
    format: "currencyNoDecimal",
    showCollapsibleSection: () => false,
    dropDownGroup: { name: "cost_of_revenue", type: "item" },
  },
  {
    key: "tax",
    label: "Tax",
    format: "currencyNoDecimal",
    dropDownGroup: { name: "cost_of_revenue", type: "item" },
    showCollapsibleSection: () => false,
    excludeFromTable: excludeNoDataFromTable,
  },
  {
    key: "gross_profit",
    label: "Gross Profit",
    format: "currencyNoDecimal",
    rowStyle: "bold",
  },
  {
    key: "gross_margin_pct",
    label: "GM %",
    format: "percentTwoDecimal",
  },
  {
    key: "gross_profit_per_visitor",
    label: "Gross Profit per Visitor",
    format: "currency",
    rowStyle: "bold",
    excludeFromTable: excludeOnOrdersMatchedToSession,
  },
];
